import styled from 'styled-components';
import type { Asset } from '../../types/ugla-types';
import { getTag } from '../../utils/tag-helpers';

interface OptionItemProps {
  option: string;
  asset: Asset | undefined;
  selected: boolean;
  onClick: () => void;
}

/**
 * Individual option item component
 * Displays a product reference option with preview image and label
 * Uses the 'thumbnail' tag if available, otherwise falls back to asset URL
 */
const OptionItem: React.FC<OptionItemProps> = ({
  option,
  asset,
  selected,
  onClick,
}) => {
  // Get thumbnail URL from asset tags, fall back to asset URL if not available
  const thumbnailUrl = asset
    ? getTag('thumbnail', asset.tags) || asset.url
    : undefined;
  
  

  return (
    <ItemContainer selected={selected} onClick={onClick}>
      <ImageContainer>
        {thumbnailUrl ? (
          <OptionImage src={thumbnailUrl} alt={option} />
        ) : (
          <PlaceholderImage>
            <PlaceholderIcon>📷</PlaceholderIcon>
          </PlaceholderImage>
        )}
        {selected && <SelectedBadge>✓</SelectedBadge>}
      </ImageContainer>
      <OptionLabel>{option}</OptionLabel>
    </ItemContainer>
  );
};

const ItemContainer = styled.div<{ selected: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.25rem;
  padding: 5px;
  background-color: ${({ selected, theme }) => (selected ? theme.colors.itemContainerSelectedBackground : theme.colors.itemContainerBackground)};
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.3s ease;
  border: ${({ selected }) => (selected ? '3px solid #ffffff' : '2px solid transparent')};
  box-shadow: ${({ selected }) =>
    selected
      ? '0 4px 12px rgba(255, 255, 255, 0.3)'
      : '0 2px 6px rgba(0, 0, 0, 0.1)'};

  &:hover {
    background-color: ${({ theme }) => theme.colors.itemContainerHoverBackground};
    transform: translateY(-4px);
    box-shadow: ${({ selected }) =>
      selected
        ? '0 6px 16px rgba(255, 255, 255, 0.4)'
        : '0 4px 12px rgba(0, 0, 0, 0.2)'};
  }

  &:active {
    transform: translateY(-2px);
  }
`;

const ImageContainer = styled.div`
  position: relative;
  width: 5vw;
  border-radius: 8px 8px 0 0;
  overflow: hidden;
  background-color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const OptionImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

const PlaceholderImage = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #f0f0f0;
`;

const PlaceholderIcon = styled.span`
  font-size: 2rem;
  opacity: 0.5;
`;

const SelectedBadge = styled.div`
  position: absolute;
  top: 4px;
  right: 4px;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background-color: ${props => props.theme.colors.selectedBadgeBackground};
  color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.875rem;
  font-weight: bold;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.3);
  border: 2px solid #ffffff;
`;

const OptionLabel = styled.span`
  color: #ffffff;
  font-size: 0.9rem;
  font-weight: 500;
  text-align: center;
  text-transform: capitalize;
  word-break: break-word;
  line-height: 1.3;
  width: 100%;
  max-width: 100%;
  @media(max-width: ${props => props.theme.breakpoints?.tablet}){display: none;}
`;

export default OptionItem;
